import {
  type Account,
  AccountPlanType,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {UsageType} from '../../generated/graphql';
import {useAccountPrivileges} from '../accounts/pricing';
import {useRampFlagEnableWbAgentUsageCharts} from '../rampFeatureFlags/rampFeatureFlags';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  useGetUsageThisBillingPeriod,
} from './utils';

function isWbAgentBillingPlanEligible(account: Account): boolean {
  return (
    account.accountPlanType === AccountPlanType.Pro ||
    account.accountPlanType === AccountPlanType.Enterprise
  );
}

/** Call only when `orgName` is known (e.g. from the URL or account selector). */
export function useIsWbAgentBillingUsageVisible(orgName: string): {
  visible: boolean;
  loading: boolean;
} {
  const {enabled, loading} = useRampFlagEnableWbAgentUsageCharts(orgName);
  return {
    visible: !loading && enabled,
    loading,
  };
}

function useWbAgentBillingUsageVisibility(orgName: string | null): {
  visible: boolean;
  loading: boolean;
} {
  const hasOrg = orgName != null;

  // When there is no org, pass null so useOrgNameRampFlag skips the query
  // (its skip condition is `orgName == null`). Treat the flag as false.
  const {enabled, loading: rampLoading} = useRampFlagEnableWbAgentUsageCharts(
    (hasOrg ? orgName : null) as string
  );

  if (!hasOrg) {
    return {visible: false, loading: false};
  }

  return {
    visible: !rampLoading && enabled,
    loading: rampLoading,
  };
}

/**
 * Hook to get WB Agent usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetWbagentUsageDetails(selectedAccount: Account | null) {
  const orgName =
    selectedAccount?.accountType === AccountType.Personal
      ? null
      : (selectedAccount?.name ?? null);
  const {visible: isRampVisible, loading: visibilityLoading} =
    useWbAgentBillingUsageVisibility(orgName);
  const isVisible =
    isRampVisible &&
    selectedAccount != null &&
    isWbAgentBillingPlanEligible(selectedAccount);
  const skip = selectedAccount?.name == null || visibilityLoading || !isVisible;

  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {
    privileges: {wbagentLimits, wbagentLimitDateInterval},
  } = privilegeResults;

  // TODO: In the future we would check for the existence of a stripe or orb subscription
  const isUsagePaid = false;

  const calendarMonth = getIntervalMarkersFromCurrentCalendarMonth();

  const finalLimitDateInterval =
    selectedAccount?.accountPlanType === AccountPlanType.Enterprise
      ? getIntervalMarkersFromLimitDateInterval(wbagentLimitDateInterval)
      : calendarMonth;

  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.WbAgentCost,
    finalLimitDateInterval,
    skip
  );

  const loading = visibilityLoading || usageResults.loading;
  const error = usageResults.error;
  const [billingPeriodStart, billingPeriodEnd] = finalLimitDateInterval;
  const {usage} = usageResults;

  return {
    isUsagePaid,
    wbagentLimits,
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    loading,
    error,
    isVisible,
    visibilityLoading,
  };
}
